import React from 'react'
import type { Block, Variable } from '../../editor/types'

interface ChartBlockProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
  variables: Variable[]
}

export type ChartType = 'bar' | 'line' | 'donut' | 'area' | 'horizontal-bar' | 'stacked-bar' | 'pie'

const DEFAULT_CATEGORIES = ['OCT', 'NOV', 'DEC']
const DEFAULT_BASELINES = [75, 60, 85]

function getConfig(block: Block) {
  const config = (block.config || {}) as Record<string, unknown>
  return {
    chartType: (config.chartType as ChartType) || 'bar',
    categories: (config.categories as string[]) || DEFAULT_CATEGORIES,
    baselines: (config.baselines as number[]) || DEFAULT_BASELINES,
    showValues: (config.showValues as boolean) ?? true,
    legendBaseline: (config.legendBaseline as string) || 'Baseline',
    legendProjected: (config.legendProjected as string) || 'Projected',
    showLegend: (config.showLegend as boolean) ?? true,
    dataMode: (config.dataMode as string) || 'ratio',
    categorySources: (config.categorySources as string[]) || [],
  }
}

export default function ChartBlock({ block, variableValues, variables }: ChartBlockProps) {
  const { chartType, categories, baselines, showValues, legendBaseline, legendProjected, showLegend, dataMode, categorySources } = getConfig(block)
  const accent = block.accent || 'var(--accent, #5048e5)'

  // In "variables" mode, read each category's value from its own variable
  // In "ratio" mode, use the single source variable as a multiplier
  const varName = block.source || ''
  const variable = variables.find((v) => v.name === varName)
  const value = Number(variableValues[varName] ?? 0)
  const max = variable?.max ?? 100
  const ratio = dataMode === 'variables' ? 0 : (max > 0 ? value / max : 0)

  // Per-category values for "variables" mode
  const categoryValues = dataMode === 'variables'
    ? categories.map((_, i) => Number(variableValues[categorySources[i] || ''] ?? 0))
    : null

  return (
    <div className="p-6">
      {/* Header + Legend */}
      <div className="flex items-center justify-between mb-6">
        <h4 className="font-bold text-slate-900 text-sm">
          {block.label || 'Chart'}
        </h4>
        {showLegend && (
          <div className="flex gap-4">
            <div className="flex items-center gap-1.5">
              <div className="size-2 bg-slate-200 rounded-full" />
              <span className="text-[11px] font-medium text-slate-500">{legendBaseline}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="size-2 rounded-full" style={{ backgroundColor: accent }} />
              <span className="text-[11px] font-medium text-slate-500">{legendProjected}</span>
            </div>
          </div>
        )}
      </div>

      {chartType === 'bar' && (
        <BarChart categories={categories} baselines={baselines} ratio={ratio} accent={accent} categoryValues={categoryValues} />
      )}
      {chartType === 'line' && (
        <LineChart categories={categories} baselines={baselines} ratio={ratio} accent={accent} categoryValues={categoryValues} />
      )}
      {chartType === 'area' && (
        <AreaChart categories={categories} baselines={baselines} ratio={ratio} accent={accent} categoryValues={categoryValues} />
      )}
      {chartType === 'donut' && (
        <DonutChart categories={categories} baselines={baselines} ratio={ratio} showValues={showValues} accent={accent} categoryValues={categoryValues} />
      )}
      {chartType === 'horizontal-bar' && (
        <HorizontalBarChart categories={categories} baselines={baselines} ratio={ratio} accent={accent} categoryValues={categoryValues} />
      )}
      {chartType === 'stacked-bar' && (
        <StackedBarChart categories={categories} baselines={baselines} ratio={ratio} accent={accent} categoryValues={categoryValues} />
      )}
      {chartType === 'pie' && (
        <PieChart categories={categories} baselines={baselines} ratio={ratio} showValues={showValues} accent={accent} categoryValues={categoryValues} />
      )}

    </div>
  )
}

/* ========== Bar Chart ========== */
function BarChart({ categories, baselines, ratio, accent, categoryValues }: { categories: string[]; baselines: number[]; ratio: number; accent: string; categoryValues: number[] | null }) {
  const maxVal = categoryValues ? Math.max(...categoryValues, 1) : 100

  return (
    <div className="h-48 w-full flex items-end gap-3 justify-between">
      {categories.map((cat, i) => {
        if (categoryValues) {
          const val = categoryValues[i] || 0
          const h = Math.round((val / maxVal) * 192)
          return (
            <div key={cat} className="flex-1 flex flex-col gap-1 items-center">
              <div className="w-full flex items-end justify-center" style={{ height: '192px' }}>
                <div
                  className="w-full rounded-t-lg transition-all duration-500"
                  style={{ backgroundColor: accent, height: `${h}px` }}
                />
              </div>
              <span className="text-[10px] text-slate-400 mt-1 font-bold">{cat}</span>
              <span className="text-[10px] font-mono text-slate-500">{val}</span>
            </div>
          )
        }
        const baselinePct = baselines[i] ?? 50
        const baselineH = Math.round((baselinePct / 100) * 192)
        const projectedH = Math.min(Math.round(baselineH * (1 + ratio * 0.5)), 192)

        return (
          <div key={cat} className="flex-1 flex flex-col gap-1 items-center">
            <div className="w-full flex items-end gap-1 justify-center" style={{ height: '192px' }}>
              <div
                className="flex-1 bg-slate-100 rounded-t-lg transition-all duration-500"
                style={{ height: `${baselineH}px` }}
              />
              <div
                className="flex-1 rounded-t-lg transition-all duration-500"
                style={{ backgroundColor: accent, height: `${projectedH}px` }}
              />
            </div>
            <span className="text-[10px] text-slate-400 mt-1 font-bold">{cat}</span>
          </div>
        )
      })}
    </div>
  )
}

/* ========== Line Chart (SVG) ========== */
function LineChart({ categories, baselines, ratio, accent, categoryValues }: { categories: string[]; baselines: number[]; ratio: number; accent: string; categoryValues: number[] | null }) {
  const w = 400
  const h = 180
  const pad = { top: 10, right: 20, bottom: 30, left: 10 }
  const plotW = w - pad.left - pad.right
  const plotH = h - pad.top - pad.bottom
  const n = categories.length

  const toPath = (pts: { x: number; y: number }[]) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')

  if (categoryValues) {
    const maxVal = Math.max(...categoryValues, 1)
    const points = categoryValues.map((v, i) => ({
      x: pad.left + (i / Math.max(n - 1, 1)) * plotW,
      y: pad.top + plotH - (v / maxVal) * plotH,
    }))
    return (
      <div className="w-full">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
          {[0, 25, 50, 75, 100].map((v) => {
            const y = pad.top + plotH - (v / 100) * plotH
            return <line key={v} x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke="#f1f5f9" strokeWidth="1" />
          })}
          <path d={toPath(points)} fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-all duration-500" />
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="4" fill={accent} stroke="#fff" strokeWidth="2" className="transition-all duration-500" />
          ))}
          {categories.map((cat, i) => {
            const x = pad.left + (i / Math.max(n - 1, 1)) * plotW
            return <text key={cat} x={x} y={h - 5} textAnchor="middle" className="text-[10px] fill-slate-400 font-bold">{cat}</text>
          })}
        </svg>
      </div>
    )
  }

  const baselinePoints = baselines.map((b, i) => ({
    x: pad.left + (i / Math.max(n - 1, 1)) * plotW,
    y: pad.top + plotH - (b / 100) * plotH,
  }))
  const projectedPoints = baselines.map((b, i) => ({
    x: pad.left + (i / Math.max(n - 1, 1)) * plotW,
    y: pad.top + plotH - (Math.min(b * (1 + ratio * 0.5), 100) / 100) * plotH,
  }))

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
        {[0, 25, 50, 75, 100].map((v) => {
          const y = pad.top + plotH - (v / 100) * plotH
          return <line key={v} x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke="#f1f5f9" strokeWidth="1" />
        })}
        <path d={toPath(baselinePoints)} fill="none" stroke="#e2e8f0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {baselinePoints.map((p, i) => (
          <circle key={`b-${i}`} cx={p.x} cy={p.y} r="4" fill="#e2e8f0" stroke="#fff" strokeWidth="2" />
        ))}
        <path d={toPath(projectedPoints)} fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-all duration-500" />
        {projectedPoints.map((p, i) => (
          <circle key={`p-${i}`} cx={p.x} cy={p.y} r="4" fill={accent} stroke="#fff" strokeWidth="2" className="transition-all duration-500" />
        ))}
        {categories.map((cat, i) => {
          const x = pad.left + (i / Math.max(n - 1, 1)) * plotW
          return <text key={cat} x={x} y={h - 5} textAnchor="middle" className="text-[10px] fill-slate-400 font-bold">{cat}</text>
        })}
      </svg>
    </div>
  )
}

/* ========== Area Chart (SVG) ========== */
function AreaChart({ categories, baselines, ratio, accent, categoryValues }: { categories: string[]; baselines: number[]; ratio: number; accent: string; categoryValues: number[] | null }) {
  const w = 400
  const h = 180
  const pad = { top: 10, right: 20, bottom: 30, left: 10 }
  const plotW = w - pad.left - pad.right
  const plotH = h - pad.top - pad.bottom
  const n = categories.length
  const bottomY = pad.top + plotH

  const toAreaPath = (pts: { x: number; y: number }[]) => {
    if (pts.length === 0) return ''
    const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
    return `${line} L${pts[pts.length - 1].x},${bottomY} L${pts[0].x},${bottomY} Z`
  }

  const toLinePath = (pts: { x: number; y: number }[]) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')

  if (categoryValues) {
    const maxVal = Math.max(...categoryValues, 1)
    const points = categoryValues.map((v, i) => ({
      x: pad.left + (i / Math.max(n - 1, 1)) * plotW,
      y: pad.top + plotH - (v / maxVal) * plotH,
    }))
    return (
      <div className="w-full">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
          {[0, 25, 50, 75, 100].map((v) => {
            const y = pad.top + plotH - (v / 100) * plotH
            return <line key={v} x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke="#f1f5f9" strokeWidth="1" />
          })}
          <path d={toAreaPath(points)} fill={accent} opacity="0.12" className="transition-all duration-500" />
          <path d={toLinePath(points)} fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-all duration-500" />
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="3.5" fill={accent} stroke="#fff" strokeWidth="2" className="transition-all duration-500" />
          ))}
          {categories.map((cat, i) => {
            const x = pad.left + (i / Math.max(n - 1, 1)) * plotW
            return <text key={cat} x={x} y={h - 5} textAnchor="middle" className="text-[10px] fill-slate-400 font-bold">{cat}</text>
          })}
        </svg>
      </div>
    )
  }

  const baselinePoints = baselines.map((b, i) => ({
    x: pad.left + (i / Math.max(n - 1, 1)) * plotW,
    y: pad.top + plotH - (b / 100) * plotH,
  }))
  const projectedPoints = baselines.map((b, i) => ({
    x: pad.left + (i / Math.max(n - 1, 1)) * plotW,
    y: pad.top + plotH - (Math.min(b * (1 + ratio * 0.5), 100) / 100) * plotH,
  }))

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
        {[0, 25, 50, 75, 100].map((v) => {
          const y = pad.top + plotH - (v / 100) * plotH
          return <line key={v} x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke="#f1f5f9" strokeWidth="1" />
        })}
        <path d={toAreaPath(baselinePoints)} fill="#f1f5f9" />
        <path d={toLinePath(baselinePoints)} fill="none" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d={toAreaPath(projectedPoints)} fill={accent} opacity="0.12" className="transition-all duration-500" />
        <path d={toLinePath(projectedPoints)} fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-all duration-500" />
        {projectedPoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3.5" fill={accent} stroke="#fff" strokeWidth="2" className="transition-all duration-500" />
        ))}
        {categories.map((cat, i) => {
          const x = pad.left + (i / Math.max(n - 1, 1)) * plotW
          return <text key={cat} x={x} y={h - 5} textAnchor="middle" className="text-[10px] fill-slate-400 font-bold">{cat}</text>
        })}
      </svg>
    </div>
  )
}

/* ========== Donut Chart (SVG) ========== */
function DonutChart({ categories, baselines, ratio, showValues, accent, categoryValues }: { categories: string[]; baselines: number[]; ratio: number; showValues: boolean; accent: string; categoryValues: number[] | null }) {
  const size = 180
  const cx = size / 2
  const cy = size / 2
  const outerR = 75
  const innerR = 50

  // Generate colors from accent
  const colors = [
    accent,
    '#94a3b8', // slate
    '#e2e8f0', // light slate
    '#c4b5fd', // violet light
    '#fbbf24', // amber
    '#34d399', // emerald
  ]

  // Compute projected values (or use categoryValues directly)
  const projected = categoryValues
    ? categoryValues.map((v) => Math.max(v, 0))
    : baselines.map((b) => Math.max(b * (1 + ratio * 0.3), 1))
  const total = projected.reduce((a, b) => a + b, 0)

  let currentAngle = -90 // start at top

  const segments = projected.map((val, i) => {
    const pct = total > 0 ? val / total : 0
    const angle = Math.min(pct * 360, 359.99) // Cap to avoid degenerate 360° arc
    const startAngle = currentAngle
    const endAngle = currentAngle + (pct * 360) // Use real angle for positioning
    currentAngle = endAngle

    const startRad = (startAngle * Math.PI) / 180
    const endRad = ((startAngle + angle) * Math.PI) / 180
    const largeArc = angle > 180 ? 1 : 0

    const x1 = cx + outerR * Math.cos(startRad)
    const y1 = cy + outerR * Math.sin(startRad)
    const x2 = cx + outerR * Math.cos(endRad)
    const y2 = cy + outerR * Math.sin(endRad)
    const x3 = cx + innerR * Math.cos(endRad)
    const y3 = cy + innerR * Math.sin(endRad)
    const x4 = cx + innerR * Math.cos(startRad)
    const y4 = cy + innerR * Math.sin(startRad)

    const d = [
      `M${x1},${y1}`,
      `A${outerR},${outerR} 0 ${largeArc} 1 ${x2},${y2}`,
      `L${x3},${y3}`,
      `A${innerR},${innerR} 0 ${largeArc} 0 ${x4},${y4}`,
      'Z',
    ].join(' ')

    return { d, color: colors[i % colors.length], label: categories[i], pct }
  })

  return (
    <div className="flex items-center gap-6 justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((seg, i) => (
          <path
            key={i}
            d={seg.d}
            fill={seg.color}
            className="transition-all duration-500"
            stroke="#fff"
            strokeWidth="2"
          />
        ))}
        {showValues && (
          <text x={cx} y={cy + 4} textAnchor="middle" className="text-lg fill-slate-900 font-bold">
            {Math.round(total)}
          </text>
        )}
      </svg>
      <div className="flex flex-col gap-2">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="size-2.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="text-xs text-slate-600">{seg.label}</span>
            <span className="text-xs text-slate-400 font-mono ml-auto">{Math.round(seg.pct * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ========== Horizontal Bar Chart ========== */
function HorizontalBarChart({ categories, baselines, ratio, accent, categoryValues }: { categories: string[]; baselines: number[]; ratio: number; accent: string; categoryValues: number[] | null }) {
  const maxVal = categoryValues ? Math.max(...categoryValues, 1) : 100

  return (
    <div className="space-y-3">
      {categories.map((cat, i) => {
        if (categoryValues) {
          const val = categoryValues[i] || 0
          const pct = Math.round((val / maxVal) * 100)
          return (
            <div key={cat} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-500">{cat}</span>
                <span className="text-[11px] font-mono text-slate-400">{val}</span>
              </div>
              <div className="relative h-6 bg-slate-100 rounded-lg overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-lg transition-all duration-500"
                  style={{ width: `${pct}%`, backgroundColor: accent, opacity: 0.8 }}
                />
              </div>
            </div>
          )
        }
        const baselinePct = baselines[i] ?? 50
        const projectedPct = Math.min(Math.round(baselinePct * (1 + ratio * 0.5)), 100)
        return (
          <div key={cat} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-500">{cat}</span>
              <span className="text-[11px] font-mono text-slate-400">{projectedPct}%</span>
            </div>
            <div className="relative h-6 bg-slate-100 rounded-lg overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-slate-200 rounded-lg transition-all duration-500"
                style={{ width: `${baselinePct}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 rounded-lg transition-all duration-500"
                style={{ width: `${projectedPct}%`, backgroundColor: accent, opacity: 0.8 }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ========== Stacked Bar Chart ========== */
function StackedBarChart({ categories, baselines, ratio, accent, categoryValues }: { categories: string[]; baselines: number[]; ratio: number; accent: string; categoryValues: number[] | null }) {
  const colors = [accent, '#94a3b8', '#e2e8f0', '#c4b5fd', '#fbbf24', '#34d399']
  const projected = categoryValues
    ? categoryValues.map((v) => Math.max(v, 0))
    : baselines.map((b) => Math.max(b * (1 + ratio * 0.3), 1))
  const total = projected.reduce((a, b) => a + b, 0)

  return (
    <div className="space-y-3">
      <div className="h-10 flex rounded-xl overflow-hidden">
        {projected.map((val, i) => {
          const pct = (val / total) * 100
          return (
            <div
              key={i}
              className="transition-all duration-500 flex items-center justify-center"
              style={{ width: `${pct}%`, backgroundColor: colors[i % colors.length] }}
            >
              {pct > 8 && (
                <span className="text-[10px] font-bold text-white drop-shadow-sm">
                  {Math.round(pct)}%
                </span>
              )}
            </div>
          )
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {categories.map((cat, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="size-2.5 rounded-full shrink-0" style={{ backgroundColor: colors[i % colors.length] }} />
            <span className="text-[11px] text-slate-600">{cat}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ========== Pie Chart (SVG) ========== */
function PieChart({ categories, baselines, ratio, showValues, accent, categoryValues }: { categories: string[]; baselines: number[]; ratio: number; showValues: boolean; accent: string; categoryValues: number[] | null }) {
  const size = 180
  const cx = size / 2
  const cy = size / 2
  const r = 80

  const colors = [accent, '#94a3b8', '#e2e8f0', '#c4b5fd', '#fbbf24', '#34d399']
  const projected = categoryValues
    ? categoryValues.map((v) => Math.max(v, 0))
    : baselines.map((b) => Math.max(b * (1 + ratio * 0.3), 1))
  const total = projected.reduce((a, b) => a + b, 0)

  let currentAngle = -90

  const segments = projected.map((val, i) => {
    const pct = total > 0 ? val / total : 0
    const angle = Math.min(pct * 360, 359.99)
    const startAngle = currentAngle
    currentAngle += pct * 360

    const startRad = (startAngle * Math.PI) / 180
    const endRad = ((startAngle + angle) * Math.PI) / 180
    const largeArc = angle > 180 ? 1 : 0

    const x1 = cx + r * Math.cos(startRad)
    const y1 = cy + r * Math.sin(startRad)
    const x2 = cx + r * Math.cos(endRad)
    const y2 = cy + r * Math.sin(endRad)

    const d = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`

    return { d, color: colors[i % colors.length], label: categories[i], pct }
  })

  return (
    <div className="flex items-center gap-6 justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((seg, i) => (
          <path
            key={i}
            d={seg.d}
            fill={seg.color}
            className="transition-all duration-500"
            stroke="#fff"
            strokeWidth="2"
          />
        ))}
      </svg>
      <div className="flex flex-col gap-2">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="size-2.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="text-xs text-slate-600">{seg.label}</span>
            {showValues && <span className="text-xs text-slate-400 font-mono ml-auto">{Math.round(seg.pct * 100)}%</span>}
          </div>
        ))}
      </div>
    </div>
  )
}
